import * as React from 'react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import axios from 'axios';
import { useFormik } from 'formik';
import * as yup from 'yup';
import "./SignOut.scss";
import { UcThemeContext } from '../../lib/UcThemeProvider';
import { UrlHelpers } from '../../lib/Helpers';
import { getUrl, useDomainNavigate, domain } from '../../domain';
import { logErrorGrayLog } from '../../lib/ClientErrorGrayLogger';
import { v4 as uuidv4 } from 'uuid';
import { confirmPasswordSchema, passwordSchema } from '../../lib/PasswordValidation';

export default function ResetPassword(props) {
    // eslint-disable-next-line no-unused-vars
    const [themeState, themeDispatch] = React.useContext(UcThemeContext);
    const [openConfirmation, setOpenConfirmation] = React.useState(false);
    const [errors, setErrors] = React.useState([]);
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    let returnUrlParam = UrlHelpers.getSafeUrl(params.get('ReturnUrl') || "", "/account/logout");
    let email = decodeURI(params.get('email'));
    const delay = ms => new Promise(res => setTimeout(res, ms));
    const navigate = useDomainNavigate();

    const handleCloseConfirmation = (event, reason) => {
      if (reason === 'clickaway') {
        return;
      }

      setOpenConfirmation(false);
    };

    const axiosconfig = {
        headers: {
            "Content-Type": "application/json",
            "SubDomain": domain.subDomain
        },
    };

    const initialValues = { code: code, email: email, password: '', confirmPassword: '' };
    const validationSchema = yup.object({
        code: yup
            .string()
            .required('Code is required'),
        email: yup
            .string('Enter your email')
            .email('Enter a valid email')
            .required('Email is required'),
        password: passwordSchema(),
        confirmPassword: confirmPasswordSchema(),
    });

    const redirectHome = async (navigateTo) => {
        await delay(4000);
        navigate(navigateTo);
    };

    const formik = useFormik({
        initialValues: initialValues,
        validationSchema: validationSchema,
        onSubmit: (values) => {
            axios.post(getUrl(`/identity/account/resetPassword`), JSON.stringify(values), axiosconfig)
                .then(response => {
                    formik.setSubmitting(false);

                    if (response.status === 200) {
                        setOpenConfirmation(true);
                        if (!returnUrlParam.toLowerCase().startsWith('/account/logout')) {
                            window.location = getUrl(decodeURIComponent(returnUrlParam));
                        }
                        else {
                            redirectHome(getUrl("/account/login/"));
                        }
                    }
                    else  {
                        setErrors(response);                        
                    }
                })
                .catch(error => {
                    if (Array.isArray(error.response.data))
                    {
                        setErrors(error.response.data);                    
                    }
                    else
                    {
                        console.log("Unexpected error", error?.response, error?.response?.data);
                        const clientError = {
                            referenceGuid: uuidv4(),
                            clientError: JSON.stringify(error),
                            clientStacktrace: "N/A ResetPassword.js",
                            url: window?.location?.href
                        };

                        logErrorGrayLog(clientError)
                        setErrors(["Unexpected error: Reference " + clientError.referenceGuid])
                    }

                    formik.setSubmitting(false);
                });
        },
    });

    return (
        <>
            <Box
                sx={{
                    marginTop: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                <Typography variant="subtitle1" gutterBottom component="div" sx={{mt:2}}>
                    Enter your new password.
                </Typography>
                <form onSubmit={formik.handleSubmit}
                >
                    {                        
                        errors?.map((error) => (
                            <Alert sx={{ mt: 2 }} severity="error">{error}</Alert>
                        ))
                    }
                    <TextField sx={{ mt: 2 }}
                        type="email"
                        name="email"
                        label="Email Address"
                        fullWidth
                        value={email}
                        disabled="true"
                    />
                    <TextField sx={{ mt: 2 }}
                        type="password"
                        name="password"
                        label="Password"
                        fullWidth
                        value={formik.values.password}
                        onChange={formik.handleChange}
                        error={formik.touched.password && Boolean(formik.errors.password)}
                        helperText={formik.touched.password && formik.errors.password}
                    />
                    <TextField sx={{ mt: 2 }}
                        type="password"
                        name="confirmPassword"
                        label="Confirm Password"
                        fullWidth
                        value={formik.values.confirmPassword}
                        onChange={formik.handleChange}
                        error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
                        helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
                    />
                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        disabled={formik.isSubmitting}
                        sx={{ mt: 3, mb: 2 }}
                    >
                        Submit
                    </Button>
                </form>
            </Box>
            <Snackbar
                open={openConfirmation}
                autoHideDuration={6000}
                onClose={handleCloseConfirmation}
            >
                <Alert severity="success">
                    Your password was set successfully.
                    Redirecting...
                </Alert>
            </Snackbar>
        </>
    );
}